import moji from 'moji';
import { toHiragana, toKatakana } from 'wanakana';

/**
 * Convert hiragana to katakana
 */
export function hiraganaToKatakana(str: string): string {
  return toKatakana(str, { passRomaji: true });
}

/**
 * Convert katakana to hiragana
 */
export function katakanaToHiragana(str: string): string {
  return toHiragana(str, { passRomaji: true });
}

/**
 * Convert full-width characters to half-width
 */
export function fullWidthToHalfWidth(str: string): string {
  return moji(str)
    .convert('ZE', 'HE') // Full-width ASCII to half-width ASCII
    .convert('ZK', 'HK') // Full-width katakana to half-width katakana
    .toString();
}

/**
 * Convert half-width characters to full-width
 */
export function halfWidthToFullWidth(str: string): string {
  return moji(str)
    .convert('HE', 'ZE') // Half-width ASCII to full-width ASCII
    .convert('HK', 'ZK') // Half-width katakana to full-width katakana
    .toString();
}

/**
 * Generate all possible variations of a Japanese text for search matching
 */
export function generateJapaneseVariations(text: string): string[] {
  const variations = new Set<string>();

  variations.add(text);

  const fullWidthText = halfWidthToFullWidth(text);
  variations.add(fullWidthText);

  const hiraganaVersion = toHiragana(fullWidthText);
  const katakanaVersion = toKatakana(fullWidthText);

  variations.add(hiraganaVersion);
  variations.add(katakanaVersion);

  variations.add(toHiragana(text));
  variations.add(toKatakana(text));

  [text, fullWidthText, hiraganaVersion, katakanaVersion].forEach((variant) => {
    variations.add(fullWidthToHalfWidth(variant));
    variations.add(halfWidthToFullWidth(variant));
  });

  return Array.from(variations);
}

/**
 * Create a regex pattern that matches all variations of Japanese text
 * @param keyword - Search keyword
 * @returns RegExp that matches all variations of the keyword
 */
export function createJapaneseSearchRegex(keyword: string): RegExp {
  const variations = generateJapaneseVariations(keyword);
  const escapedVariations = variations.map((variation) =>
    variation.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
  );
  const pattern = `(${escapedVariations.join('|')})`;
  return new RegExp(pattern, 'gi');
}
